import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Beaker, Pill, Syringe, Activity, FlaskConical } from "lucide-react";
import products from "@/data/products";
import ProductCard from "@/components/ProductCard";
import ScrollReveal from "@/components/ScrollReveal";

export default function ProductDetail() {
  const { id } = useParams();
  const product = products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Product Not Found</h2>
          <Link to="/products" className="text-[#2DD4BF] hover:underline">Back to Products</Link>
        </div>
      </div>
    );
  }

  const related = products
    .filter(p => p.id !== product.id && p.category === product.category)
    .slice(0, 3);

  const infoCards = [
    { label: "Salt / Generic Name", value: product.salt, icon: FlaskConical, highlight: true },
    { label: "Molecular Formation", value: product.molecularFormation, icon: Beaker, mono: true },
    { label: "Dosage", value: product.dosage, icon: Pill },
    { label: "Administration", value: product.administration, icon: Syringe },
    { label: "Category", value: product.category, icon: Activity },
  ];

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-16 md:pt-0">
      {/* Back nav */}
      <div className="max-w-7xl mx-auto px-6 pt-24 md:pt-32">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <Link
            to="/products"
            data-testid="back-to-products"
            className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#2DD4BF] transition-colors mb-10"
          >
            <ArrowLeft size={16} /> Back to Products
          </Link>
        </motion.div>
      </div>

      {/* Product hero */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="aspect-square rounded-xl overflow-hidden border border-white/5 bg-[#0F172A] flex items-center justify-center p-10">
              <img
                src={product.image}
                alt={product.name}
                className="max-w-full max-h-full object-contain"
                data-testid="product-detail-image"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1559757175-7cb057fba93c?auto=format&fit=crop&w=400&q=80';
                }}
              />
            </div>
          </motion.div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <span className="text-xs font-bold tracking-[0.15em] text-[#94A3B8] uppercase">{product.category}</span>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mt-2 mb-2" data-testid="product-detail-name">
              {product.name}
            </h1>
            <p className="text-lg text-[#2DD4BF] font-semibold mb-6" data-testid="product-detail-salt">
              {product.salt}
            </p>
            <p className="text-[#94A3B8] leading-relaxed text-base mb-10" data-testid="product-detail-description">
              {product.description}
            </p>

            {/* Info cards */}
            <div className="space-y-3">
              {infoCards.map((card) => (
                <div
                  key={card.label}
                  data-testid={`info-${card.label.toLowerCase().replace(/[\s\/]/g,'-')}`}
                  className={`p-4 rounded-lg border transition-colors flex items-start gap-4 ${
                    card.highlight
                      ? "border-[#2DD4BF]/20 bg-[#2DD4BF]/[0.03]"
                      : "border-white/5 bg-white/[0.02] hover:border-white/10"
                  }`}
                >
                  <card.icon size={18} className={card.highlight ? "text-[#2DD4BF] mt-0.5" : "text-[#94A3B8] mt-0.5"} />
                  <div>
                    <span className="text-[10px] font-bold tracking-[0.12em] text-[#94A3B8] uppercase block mb-1">{card.label}</span>
                    <p className={`text-sm text-white leading-relaxed ${card.mono ? "font-mono text-xs" : ""}`}>
                      {card.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/10">
              <p className="text-[11px] text-yellow-200/60 leading-relaxed">
                This product information is for healthcare professionals only. Always follow the prescribed dosage
                and consult your physician before use. For full prescribing information, please contact Drukst Biotech.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="py-16 px-6 border-t border-white/5 pb-32">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal>
              <h2 className="text-2xl font-bold text-white mb-8">Related Products</h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p, i) => (
                <ScrollReveal key={p.id} delay={i * 80}>
                  <ProductCard product={p} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
